'use client'
import { useState } from 'react'
import Button from '@/components/Button/Button'
import Input from '@/components/Input/Input'
import styles from './EmailSignupInput.module.scss'
import useI18n from '@/hooks/use-i18n'
import FormResult from './FormResults/FormResults'
import { useParams } from 'next/navigation'
import classNames from 'classnames'
import useCurrentPage from '@/hooks/use-current-page'
import { isValidEmail } from '@/utils/strings/email'
import { useNewsletterLocations } from '@/hooks/useNewsletterLocations'
import { useFormSubmissionState } from '@/hooks/useFormSubmissionState'

const EmailSignupInput = ({ className, variant = 'emailSignupSection', inputId }: EmailSignupInputProps) => {
  const params = useParams()
  const { i18n } = useI18n()
  const { currentLanguage } = useCurrentPage()
  const { loading, formSubmit, formSubmitSuccess, startSubmission, finishSubmission } = useFormSubmissionState()

  const { locations } = useNewsletterLocations()

  const [formData, setFormData] = useState({
    email: '',
    language: currentLanguage,
  })
  const [memberAlreadySubscribed, setMemberAlreadySubscribed] = useState(false)
  const [validationErrors, setValidationErrors] = useState<{ field: string; errorMessage: string }[]>([])
  const [isFocused, setIsFocused] = useState(false)

  const validate = (field: string, value: string) => {
    let isValid = true
    let errorMessage = ''

    setValidationErrors(prevErrors => prevErrors.filter(error => error.field !== field))

    if (field === 'email') {
      if (!isValidEmail(value)) {
        isValid = false
        errorMessage = i18n('invalidEmailAddress') || ''
      }
    }

    if (!isValid) {
      setValidationErrors(prevErrors => [...prevErrors, { field, error: true, errorMessage }])
    } else {
      setValidationErrors([])
    }

    return isValid
  }

  const handleInputChange = (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: value,
    }))
  }

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Prevent resubmission
    if (formSubmitSuccess || loading) {
      return
    }

    const emailIsValid = validate('email', formData['email'])

    if (!emailIsValid) {
      finishSubmission(false)
      return
    }

    startSubmission()
    try {
      const currentLocation =
        typeof params.restaurantSlug === 'string'
          ? (locations.find(loc => loc.slug === params.restaurantSlug)?.label ?? params.restaurantSlug)
          : ''

      const response = await fetch('/api/newsletter-sign-up-klaviyo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ...(currentLocation && { location: currentLocation.trim() }),
        }),
      })
      const data = await response.json()

      // Handle 409 Conflict - user already subscribed to this location
      if (response.status === 409) {
        setMemberAlreadySubscribed(true)
        finishSubmission(true) // Show success since they're already subscribed
        return
      }

      // Handle other error status codes (400, 500, 503)
      if (!response.ok) {
        console.error('Newsletter signup failed:', data?.message || response.statusText)
        setMemberAlreadySubscribed(false)
        finishSubmission(false)
        return
      }

      setMemberAlreadySubscribed(false)
      finishSubmission(data?.success === true)
      /**
       * SEND GTM EVENT
       */
      if (data?.success) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'email_submission',
        })
      }
    } catch (error) {
      setMemberAlreadySubscribed(false)
      finishSubmission(false)
      console.error({ error })
    }
  }

  const emailError = validationErrors.find(error => error.field === 'email')?.errorMessage

  return (
    <div
      className={classNames(className, styles.EmailSignupInput)}
      data-variant={variant}
    >
      <form
        onSubmit={e => onSubmit(e)}
        className={classNames(styles.formContainer, {
          [styles.focused]: isFocused,
        })}
        noValidate
        autoComplete="on"
        name="emailSignupForm"
      >
        <div className={styles.inputContainer}>
          <Input
            className={styles.emailInput}
            value={formData.email}
            onChange={handleInputChange('email')}
            placeholder={i18n('typeYourEmail')}
            id={inputId}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>
        <Button
          type="submit"
          primary
          icon="caretRight"
          label={loading ? i18n('pleaseWait') : i18n('signUp')}
          disabled={loading}
        />
      </form>
      <div className={styles.FormResults}>
        {emailError ? (
          <FormResult
            status="error"
            errorMessage={
              <>
                <p>{emailError}</p>
              </>
            }
          />
        ) : formSubmit && !loading ? (
          formSubmitSuccess ? (
            <FormResult
              id="email_signup_success"
              variant={variant === 'simple' ? 'neutral' : 'default'}
              status="success"
              successMessage={
                memberAlreadySubscribed ? (
                  <>
                    <p>{i18n('emailAlreadySubscribed')}</p>
                  </>
                ) : (
                  <>
                    <p>{i18n('thankYou')}</p>
                  </>
                )
              }
            />
          ) : (
            <FormResult
              status="error"
              errorMessage={
                <>
                  <p>{i18n('somethingWentWrong')}</p>
                </>
              }
            />
          )
        ) : null}
      </div>
    </div>
  )
}

export default EmailSignupInput
